import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import type { PackagesOptionsViewsByRoom } from 'selectors/offerPage/multipleRoomBookingSelectors'
import { getPackageStockBudgets } from 'lib/offer/packageStockBudgets'

export type FlashPackageViewsByOccupancy = PackagesOptionsViewsByRoom
export type FlashSelectionIssueReason = 'missing-selection' | 'unavailable' | 'stock-exceeded' | 'stock-unavailable'

const MAX_SINGLE_ROOM_SELECTION_QUANTITY = 3

export interface FlashSelectionIssue {
  room: OfferPageRoom
  reason: FlashSelectionIssueReason
}

export interface FlashPackageSelection {
  allocations: Array<{ room: OfferPageRoom, view: App.OfferPackageOptionView }>
  views: Array<App.OfferPackageOptionView | undefined>
  issues: Array<FlashSelectionIssue>
  complete: boolean
}

export type FlashPackageSelectionResult =
  | { ok: true, rooms: Array<OfferPageRoom> }
  | { ok: false, rooms: Array<OfferPageRoom>, reason: FlashSelectionIssueReason | 'invalid-quantity' }

interface SelectionInput {
  rooms: Array<OfferPageRoom>
  viewsByOccupancy: FlashPackageViewsByOccupancy
  requiresRates: boolean
  checkInventory?: boolean
}

export function isCompatibleFlashPackage(view: App.OfferPackageOptionView, requiresRates: boolean): boolean {
  return view.capacityValid && view.hotelAvailable && (!requiresRates || !!view.hasAvailableRates)
}

/** Undated quantities are indicative; dated searches require confirmed package inventory. */
export function getFlashSingleRoomQuantityLimit(view: App.OfferPackageOptionView, requiresRates: boolean, checkInventory: boolean): number {
  if (!isCompatibleFlashPackage(view, requiresRates)) return 0
  // An undated view may contain stock from one cached calendar quote, not a package-wide limit.
  const availableRooms = checkInventory ? view.availableRooms ?? 0 : MAX_SINGLE_ROOM_SELECTION_QUANTITY
  return Math.max(0, Math.min(MAX_SINGLE_ROOM_SELECTION_QUANTITY, Math.floor(availableRooms)))
}

export function getFlashPackageQuantity(rooms: Array<OfferPageRoom>, packageView: App.OfferPackageOptionView): number {
  return rooms.filter(room => room.roomId === packageView.package.id && room.rateId === packageView.package.roomRate?.id).length
}

/** One-room URL package or room-type id, applied only while that room has no selection yet. */
export function getUrlFlashPackageSelection(
  rooms: Array<OfferPageRoom>,
  packageOptions: ReadonlyArray<App.OfferPackageOptionView>,
  roomId?: string,
  rateId?: string,
): Array<OfferPageRoom> | undefined {
  const room = rooms[0]
  if (rooms.length !== 1 || !room || room.roomId || room.rateId || !roomId || !rateId) return undefined
  const matches = packageOptions.filter(view => view.package.roomRate?.id === rateId && (view.package.id === roomId || view.package.roomType?.id === roomId))
  const pkg = matches.length === 1 ? matches[0].package : undefined
  return pkg?.roomRate ? [{ ...room, roomId: pkg.id, rateId: pkg.roomRate.id }] : undefined
}

/** Exact choices with aggregate inventory accounting, never a cheapest-package fallback. */
export function resolveFlashPackageSelection({ rooms, viewsByOccupancy, requiresRates, checkInventory = requiresRates }: SelectionInput): FlashPackageSelection {
  const available = Object.values(viewsByOccupancy).flat().filter(view => isCompatibleFlashPackage(view, requiresRates))
  const { pkgOptionAvailableRooms, roomTypeAvailableRooms } = getPackageStockBudgets(available)
  const views: FlashPackageSelection['views'] = rooms.map(() => undefined)
  const issues: Array<FlashSelectionIssue> = []
  // Preserve larger parties' inventory priority and stable requested order.
  const ordered = rooms.map((room, index) => ({ room, index })).sort((a, b) =>
    (b.room.adults + (b.room.teenagers ?? 0) + (b.room.children ?? 0) + (b.room.infants ?? 0)) -
    (a.room.adults + (a.room.teenagers ?? 0) + (a.room.children ?? 0) + (a.room.infants ?? 0)),
  )
  for (const { room, index } of ordered) {
    if (!room.roomId || !room.rateId) {
      issues.push({ room, reason: 'missing-selection' })
      continue
    }
    const view = viewsByOccupancy[room.id]?.find(candidate =>
      candidate.package.id === room.roomId && candidate.package.roomRate?.id === room.rateId && isCompatibleFlashPackage(candidate, requiresRates),
    )
    if (!view) {
      issues.push({ room, reason: 'unavailable' })
      continue
    }
    if (checkInventory) {
      if (view.availableRooms === undefined) {
        issues.push({ room, reason: 'stock-unavailable' })
        continue
      }
      const key = view.package.uniqueKey
      const roomTypeId = view.package.roomType.id
      if (pkgOptionAvailableRooms[key] <= 0 || roomTypeAvailableRooms[roomTypeId] <= 0) {
        issues.push({ room, reason: 'stock-exceeded' })
        continue
      }
      pkgOptionAvailableRooms[key] -= 1
      roomTypeAvailableRooms[roomTypeId] -= 1
    }
    views[index] = view
  }
  return {
    views,
    allocations: rooms.flatMap((room, index) => {
      const view = views[index]
      return view ? [{ room, view }] : []
    }),
    issues,
    complete: rooms.length > 0 && issues.length === 0,
  }
}

export function removeInvalidFlashAllocations(input: SelectionInput): Array<OfferPageRoom> {
  const invalidIds = new Set(resolveFlashPackageSelection(input).issues.filter(issue => issue.reason !== 'missing-selection').map(issue => issue.room.id))
  return input.rooms.map(room => invalidIds.has(room.id) ? { ...room, roomId: undefined, rateId: undefined } : room)
}

/** Single-room D radio choice; multi-room modes use their existing product owners. */
export function setFlashPackageQuantity({ rooms, viewsByOccupancy, packageView, quantity, requiresRates, checkInventory = requiresRates }: SelectionInput & {
  packageView: App.OfferPackageOptionView
  quantity: number
}): FlashPackageSelectionResult {
  const current = getFlashPackageQuantity(rooms, packageView)
  if (rooms.length !== 1 || (quantity !== 0 && quantity !== 1)) return { ok: false, rooms, reason: 'invalid-quantity' }
  if (quantity === 0) return { ok: true, rooms: current ? [{ ...rooms[0], roomId: undefined, rateId: undefined }] : rooms }
  const view = viewsByOccupancy[rooms[0].id]?.find(candidate => candidate.package.uniqueKey === packageView.package.uniqueKey && isCompatibleFlashPackage(candidate, requiresRates))
  if (!view) return { ok: false, rooms, reason: 'unavailable' }
  const nextRooms = current ? rooms : [{ ...rooms[0], roomId: view.package.id, rateId: view.package.roomRate.id }]
  const resolution = resolveFlashPackageSelection({ rooms: nextRooms, viewsByOccupancy, requiresRates, checkInventory })
  if (getFlashPackageQuantity(resolution.allocations.map(({ room }) => room), packageView) !== quantity) {
    const issue = resolution.issues.find(({ room }) => room.roomId === packageView.package.id && room.rateId === packageView.package.roomRate?.id)
    return { ok: false, rooms, reason: issue?.reason ?? 'unavailable' }
  }
  return { ok: true, rooms: nextRooms }
}
